/**
 * @fileoverview Zod schema for User Profile entity.
 */
import { z } from 'zod';

/**
 * Life area and daily metric names
 */
export type LifeAreaKey =
  | 'Health'
  | 'Mental & Emotional'
  | 'Partner & Love'
  | 'Family'
  | 'Friends'
  | 'Mission'
  | 'Experiences'
  | 'Spirit'
  | 'Finances'
  | 'Learning'
  | 'Daily_Clarity'
  | 'Daily_Energy'
  | 'Daily_Necessity'
  | 'Daily_Productivity'
  | 'Daily_Influence'
  | 'Daily_Courage'
  | 'Daily_Movement'
  | 'Daily_Mood'
  | 'Daily_Sleep'
  | 'Daily_Nutrition';

/**
 * Life area value and coverage tracking
 */
export interface LifeAreaValue {
  current_value?: number; // User's rating (0-10)
  coverage?: number; // How much we know (0-100)
  date?: string; // Last update timestamp (ISO 8601)
}

/**
 * Life areas and daily metrics JSONB structure from user_profiles.life_areas
 * Each field contains current_value, coverage, and date
 */
export interface LifeAreas {
  // Life Areas
  Health?: LifeAreaValue;
  'Mental & Emotional'?: LifeAreaValue;
  'Partner & Love'?: LifeAreaValue;
  Family?: LifeAreaValue;
  Friends?: LifeAreaValue;
  Mission?: LifeAreaValue;
  Experiences?: LifeAreaValue;
  Spirit?: LifeAreaValue;
  Finances?: LifeAreaValue;
  Learning?: LifeAreaValue;
  // Daily Metrics
  Daily_Clarity?: LifeAreaValue;
  Daily_Energy?: LifeAreaValue;
  Daily_Necessity?: LifeAreaValue;
  Daily_Productivity?: LifeAreaValue;
  Daily_Influence?: LifeAreaValue;
  Daily_Courage?: LifeAreaValue;
  Daily_Movement?: LifeAreaValue;
  Daily_Mood?: LifeAreaValue;
  Daily_Sleep?: LifeAreaValue;
  Daily_Nutrition?: LifeAreaValue;
  [key: string]: unknown;
}

/**
 * Onboarding chat completion tracking
 */
export interface OnboardingChatState {
  completed?: boolean;
  last_session_id?: string | null;
  completed_at?: string | null;
}

/**
 * Primary tour state with drop-off tracking
 */
export interface PrimaryTourState {
  status?: 'in-progress' | 'completed' | 'dismissed' | null;
  started_at?: string | null;
  completed_at?: string | null;
}

/**
 * Walkthrough state JSONB structure from user_profiles
 * Tracks: onboarding_chat (chat flow) and primary-tour (UI tour)
 */
export interface WalkthroughState {
  onboarding_chat?: OnboardingChatState;
  'primary-tour'?: PrimaryTourState | string; // string for legacy compat
  [key: string]: unknown;
}

/**
 * Zod schemas for validation
 */
const LifeAreaValueSchema = z.object({
  current_value: z.number().optional(),
  coverage: z.number().optional(),
  date: z.string().optional(),
});

const LifeAreasSchema = z
  .object({
    // Life Areas
    Health: LifeAreaValueSchema.optional(),
    'Mental & Emotional': LifeAreaValueSchema.optional(),
    'Partner & Love': LifeAreaValueSchema.optional(),
    Family: LifeAreaValueSchema.optional(),
    Friends: LifeAreaValueSchema.optional(),
    Mission: LifeAreaValueSchema.optional(),
    Experiences: LifeAreaValueSchema.optional(),
    Spirit: LifeAreaValueSchema.optional(),
    Finances: LifeAreaValueSchema.optional(),
    Learning: LifeAreaValueSchema.optional(),
    // Daily Metrics
    Daily_Clarity: LifeAreaValueSchema.optional(),
    Daily_Energy: LifeAreaValueSchema.optional(),
    Daily_Necessity: LifeAreaValueSchema.optional(),
    Daily_Productivity: LifeAreaValueSchema.optional(),
    Daily_Influence: LifeAreaValueSchema.optional(),
    Daily_Courage: LifeAreaValueSchema.optional(),
    Daily_Movement: LifeAreaValueSchema.optional(),
    Daily_Mood: LifeAreaValueSchema.optional(),
    Daily_Sleep: LifeAreaValueSchema.optional(),
    Daily_Nutrition: LifeAreaValueSchema.optional(),
  })
  .passthrough();

const OnboardingChatStateSchema = z.object({
  completed: z.boolean().optional(),
  last_session_id: z.string().nullable().optional(),
  completed_at: z.string().nullable().optional(),
});

const PrimaryTourStateSchema = z.object({
  status: z.enum(['in-progress', 'completed', 'dismissed']).nullable().optional(),
  started_at: z.string().nullable().optional(),
  completed_at: z.string().nullable().optional(),
});

const WalkthroughStateSchema = z
  .object({
    onboarding_chat: OnboardingChatStateSchema.optional(),
    // Allow string for legacy compat or object for new format
    'primary-tour': z.union([PrimaryTourStateSchema, z.string()]).optional(),
  })
  .passthrough();

/**
 * Notification quiet hours stored in user_profiles.notification_quiet_hours
 * During these hours (user local time), the AI coach will not send push notifications.
 */
export interface NotificationQuietHours {
  start?: number; // Hour 0-23 when quiet period begins (e.g. 22 = 10pm)
  end?: number; // Hour 0-23 when quiet period ends (e.g. 7 = 7am)
}

/**
 * Task display preferences stored in user_profiles.task_display_prefs
 * Synced across devices via Supabase.
 */
export interface TaskDisplayPrefs {
  filter?: 'today' | 'week' | 'all' | 'overdue' | 'no_due' | 'future';
  completion?: 'all' | 'done' | 'undone';
  sort?: 'alpha' | 'due' | 'updated' | 'est' | 'goal';
  sort_asc?: boolean;
  goal_filter_ids?: string[];
}

const NotificationQuietHoursSchema = z
  .object({
    start: z.number().int().min(0).max(23).optional(),
    end: z.number().int().min(0).max(23).optional(),
  })
  .passthrough();

const TaskDisplayPrefsSchema = z
  .object({
    filter: z.enum(['today', 'week', 'all', 'overdue', 'no_due', 'future']).optional(),
    completion: z.enum(['all', 'done', 'undone']).optional(),
    sort: z.enum(['alpha', 'due', 'updated', 'est', 'goal']).optional(),
    sort_asc: z.boolean().optional(),
    goal_filter_ids: z.array(z.string()).optional(),
  })
  .passthrough();

export const UserProfileSchema = z
  .object({
    user_id: z.string(),
    email: z.string().email().optional().nullable(),
    full_name: z.string().optional().nullable(),
    first_name: z.string().optional().nullable(),
    last_name: z.string().optional().nullable(),
    timezone: z.string().optional().nullable(),
    walkthrough_state: WalkthroughStateSchema.nullable().optional(),
    life_areas: LifeAreasSchema.nullable().optional(),
    task_display_prefs: TaskDisplayPrefsSchema.nullable().optional(),
    first_day_of_week: z.number().int().min(0).max(1).optional().nullable(),
    calendar_sync_enabled: z.boolean().optional().nullable(),
    calendar_privacy_mode: z.boolean().optional().nullable(),
    calendar_selected_ids: z.array(z.string()).optional().nullable(),
    calendar_last_sync_at: z.string().optional().nullable(),
    calendar_colors: z.record(z.string(), z.string()).optional().nullable(),
    notification_quiet_hours: NotificationQuietHoursSchema.nullable().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    // Allow additional columns
  })
  .passthrough();

export type UserProfile = z.infer<typeof UserProfileSchema>;
